import {useEffect, useState, forwardRef, useImperativeHandle, useCallback} from 'react';
import {useDispatch, useSelector} from 'react-redux';
import {useParams} from 'react-router-dom';
import PageBase from "../../../components/PageBase/index";
import bullet from '../../../assets/title-bullet.png';
import * as heatmapActions from "../../../redux/Heatmap/actions";
import * as ROUTES from "../../../utils/routes";
import EditFieldGroup from "../../../components/EditFieldGroup";
import ObjectSummaryRow from "../../../components/ObjectSummaryRow";
import EditField from "../../../components/EditFieldGroup/EditField";

import './EditHeatmap.scss';
import Button from "../../../components/Button";
import LoadingPanel from "../../../components/LoadingPanel";
import Toaster from "../../../components/Toaster";
import AccountSelect from "../../../components/AccountSelect";
import ContactSelect from "../../../components/ContactSelect";
import {loadHeatmapPicklists} from "../../../redux/Heatmap/actions";
import DatePicker from "react-datepicker";
import {EngagementStatus} from '../../../utils/enums.js'
const EditHeatmap = forwardRef((props, ref) => {
	const { onClose, editMode, forceReload } = props;
	const [editHeatmap, setEditHeatmap] = useState({...props.heatmap});
	const [resetDropdowns, setResetDropdowns] = useState(1);
	const [selectedVendor, setSelectedVendor] = useState(editHeatmap.Account_Name__c || '-1');
	const [selectedContact, setSelectedContact] = useState(null);
	const [selectedIntroContact, setSelectedIntroContact] = useState(null);
	const [nextStepPOC, setNextStepPOC] = useState(null);
	const [updatedFields, setUpdatedFields] = useState({});
	const user = useSelector(state => state.user.user);
	const [loading, setLoading] = useState(true);
	const dispatch = useDispatch();
	const params = useParams();

	const picklists = useSelector(state => state.heatmap.picklists);

	useEffect(() => {
		const load = async () => {
			const createFieldObject=(field)=> field ? {label:field, value:'fakeId' } : null;

			if(!editMode && picklists != null) {
				const customer = user.customers.find(c => c.Name === user.accountName);
				if (customer != null) {
				  editHeatmap.Related_Account__c = customer.Id;
				} else {
				  editHeatmap.Related_Account__c = null;
				}				editHeatmap.Next_Step_Date__c = new Date();
				editHeatmap.Next_Touchbase__c = new Date();
				editHeatmap.Touch_Base_Date__c = new Date();
				editHeatmap.Last_Touchbase__c = new Date();
				editHeatmap.Target_Date__c = new Date();

				editHeatmap.Priority__c = picklists['Priority__c'][0].value;
				editHeatmap.Preferred_Channel__c = picklists['Preferred_Channel__c'][0].value;
				editHeatmap.Engagement_Status__c = picklists['Engagement_Status__c'][0].value;
				editHeatmap.Relationship_Strength__c = picklists['Relationship_Strength__c'][0].value;
				editHeatmap.Communication_Interval__c = picklists['Communication_Interval__c'][0].value;
				editHeatmap.Touchbase_Timing__c = picklists['Touchbase_Timing__c'][0].value;
			}

			if(editMode) {
				editHeatmap.Next_Step_Date__c = new Date(editHeatmap.Next_Step_Date__c);
				editHeatmap.Next_Touchbase__c = new Date(editHeatmap.Next_Touchbase__c);
				editHeatmap.Touch_Base_Date__c = new Date(editHeatmap.Touch_Base_Date__c);
				editHeatmap.Last_Touchbase__c = new Date(editHeatmap.Last_Touchbase__c);
				editHeatmap.Target_Date__c = new Date(editHeatmap.Target_Date__c);

				setSelectedContact(createFieldObject(editHeatmap.Contact__r?.Name))
				setNextStepPOC(createFieldObject(editHeatmap.Next_Step_POC__r?.Name))
				setSelectedIntroContact(createFieldObject(editHeatmap.Intro_By__r?.Name))
			}
		};
		load();
	}, [picklists]);

	useEffect(() => {
		const load = async () => {
			await dispatch(heatmapActions.loadHeatmapPicklists());
			setLoading(false);
		};
		load();
	}, []);

	const checkRequiredFields = () => {
		if(selectedVendor === '-1') {
			Toaster.showWarn('Leading Cloud is required');
			return true;
		}
		else if(!selectedContact) {
			Toaster.showWarn('Contact is required');
			return true;
		}
		if (editHeatmap.Touch_Base_Date__c && editHeatmap.Engagement_Status__c !== EngagementStatus.Contacted) {
			Toaster.showWarn('Engagement Status has to be set to "Contacted" because Touch Base Date was provided.');
			return true;
		}
	};

	const heatmapCreated = (result) => {
		setLoading(false);
		if(result.status === "ok") {
			Toaster.showSuccess("Heatmap created successfully.")
			forceReload()
			onClose();
		}
		else {
			Toaster.showError(result.error.message);
		}
	};

	const heatmapUpdated = (result) => {
		setLoading(false);
		if(result.status === "ok") {
			Toaster.showSuccess("Heatmap updated successfully.")
			onClose();
		}
		else {
			Toaster.showError(result.error.message);
		}
	};

	const createHeatmap = useCallback(() => {
		// validation
		if(checkRequiredFields()){
			return;
		}
		setLoading(true);
		dispatch(heatmapActions.createHeatmap(editHeatmap, heatmapCreated));
	}, [editHeatmap, dispatch, selectedVendor, selectedContact]);

	const updateHeatmap = useCallback(() => {
		if(checkRequiredFields()){
			return;
		}
		setLoading(true);
		dispatch(heatmapActions.saveHeatmap(editHeatmap.Id, updatedFields, selectedVendor, heatmapUpdated));
	}, [editHeatmap, updatedFields, selectedVendor, dispatch, selectedContact]);

	// Expose save method to parent via ref
	useImperativeHandle(ref, () => ({
		save: editMode ? updateHeatmap : createHeatmap
	}), [editMode, updateHeatmap, createHeatmap]);

	if(loading) {
		return (
			<div>
				<LoadingPanel hide />
			</div>
		)
	}

	const updateField = (field, value) => {
		// update the edit object for visual
		const temp = {...editHeatmap};
		temp[field] = value;
		setEditHeatmap(temp);

		// For edit mode: update only changed fields object for the save action
		const temp1 = {...updatedFields};
		temp1[field] = value;
		setUpdatedFields(temp1);

		setTimeout(() => {console.log(updatedFields);}, 1000);
	};

	const updateVendor = (e) => {
		const vendor = e.target.value;
		setSelectedVendor(vendor);
		updateField('Account_Name__c', vendor);
		// const text = e.target.selectedOptions[0].text;
		setResetDropdowns(resetDropdowns + 1);
		setSelectedContact(null);
		setSelectedIntroContact(null);
		setNextStepPOC(null);
	};


	const onChangeContact = (option) => {
		setSelectedContact(option);
		updateField('Contact__c', option.value);
	}

	const onChangeIntroContact = (option) => {
		setSelectedIntroContact(option);
		updateField('Intro_By__c', option.value);
	}

	const onChangeNextStepPOC = (option) => {
		setNextStepPOC(option);
		updateField('Next_Step_POC__c', option.value);
	}

	return (
		<>
			<div className="edit-heatmap-container">
				<EditFieldGroup title="Details">
					{
						editMode &&
						<EditField title="Heatmap Owner">
							<div className="edit-field-styles-readonly">{editHeatmap.Owner_First_Name__c}</div>
						</EditField>
					}

					<EditField required title="Leading Cloud">
								<select className="edit-field-styles select-primary" value={selectedVendor} onChange={updateVendor}>
									<option value="-1">Please Select</option>
									<option value="AWS">AWS</option>
									<option value="Microsoft">Microsoft</option>
								</select>
					</EditField>

					<EditField title="Contact">
								<ContactSelect reset={resetDropdowns} value={selectedContact} onChange={onChangeContact} disabled={selectedVendor === '-1'} vendor={selectedVendor} />
					</EditField>

					<EditField title="Request Reason">
						<select className="edit-field-styles select-primary" value={editHeatmap.Request_Reason__c || ''} onChange={(e) => updateField('Request_Reason__c', e.target.value)}>
						<option value="">None</option>
							{ picklists['Request_Reason__c'].map((opt, i) => (<option key={i} value={opt.value}>{opt.label}</option>)) }
						</select>
					</EditField>
					<EditField title="Intro By">
								<ContactSelect reset={resetDropdowns} value={selectedIntroContact} onChange={onChangeIntroContact} disabled={selectedVendor === '-1'} vendor={selectedVendor} />
					</EditField>

					<EditField title="Priority">
						<select className="edit-field-styles select-primary" value={editHeatmap.Priority__c || ''} onChange={(e) => updateField('Priority__c', e.target.value)}>
							{ picklists['Priority__c'].map((opt, i) => (<option key={i} value={opt.value}>{opt.label}</option>)) }
						</select>
					</EditField>
						{console.log('picklists[Engagement_Status__c]',picklists['Engagement_Status__c'])}
					<EditField title="Preferred Channel">
						<select className="edit-field-styles select-primary" value={editHeatmap.Preferred_Channel__c || ''} onChange={(e) => updateField('Preferred_Channel__c', e.target.value)}>
							{ picklists['Preferred_Channel__c'].map((opt, i) => (<option key={i} value={opt.value}>{opt.label}</option>)) }
						</select>
					</EditField>

					<EditField title="Engagement Status">
						<select className="edit-field-styles select-primary" value={editHeatmap.Engagement_Status__c || ''} onChange={(e) => updateField('Engagement_Status__c', e.target.value)}>
							{ picklists['Engagement_Status__c'].map((opt, i) => (<option key={i} value={opt.value}>{opt.label}</option>)) }
						</select>
					</EditField>

					<EditField title="Heatmap Heat">
						<select className="edit-field-styles select-primary" value={editHeatmap.Heatmap_Heat__c|| ''} onChange={(e) => updateField('Heatmap_Heat__c', e.target.value)}>
						<option value="">None</option>
							{ picklists['Heatmap_Heat__c'].map((opt, i) => (<option key={i} value={opt.value}>{opt.label}</option>)) }
						</select>
					</EditField>

					<EditField title="Relationship Strength">
						<select className="edit-field-styles select-primary" value={editHeatmap.Relationship_Strength__c || ''} onChange={(e) => updateField('Relationship_Strength__c', e.target.value)}>
							{ picklists['Relationship_Strength__c'].map((opt, i) => (<option key={i} value={opt.value}>{opt.label}</option>)) }
						</select>
					</EditField>

					<EditField title="Description">
						<input type="text" className="edit-field-styles" value={editHeatmap.Description__c || ''} onChange={(e) => updateField('Description__c', e.target.value)} />
					</EditField>
				</EditFieldGroup>

				<EditFieldGroup title="Communication Timing">
					<EditField title="Target Date">
						<DatePicker
							className="date-field-styles"
							placeholderText="Pick Date"
							selected={editHeatmap.Target_Date__c}
							onChange={date => updateField('Target_Date__c', date)}
							popperClassName="datePickerCalendar"
							disabledKeyboardNavigation
							monthsShown={1}
							dateFormat="P"
						/>
					</EditField>
					<EditField title="Touch Base Date">
						<DatePicker
							className="date-field-styles"
							placeholderText="Pick Date"
							selected={editHeatmap.Touch_Base_Date__c}
							onChange={date => updateField('Touch_Base_Date__c', date)}
							popperClassName="datePickerCalendar"
							disabledKeyboardNavigation
							monthsShown={1}
							dateFormat="P"
						/>
					</EditField>

					<EditField title="Communication Interval">
						<select className="edit-field-styles select-primary" value={editHeatmap.Communication_Interval__c || ''} onChange={(e) => updateField('Communication_Interval__c', e.target.value)}>
							{ picklists['Communication_Interval__c'].map((opt, i) => (<option key={i} value={opt.value}>{opt.label}</option>)) }
						</select>
					</EditField>
					<EditField title="Touchbase Timing">
						<select className="edit-field-styles select-primary" value={editHeatmap.Touchbase_Timing__c || ''} onChange={(e) => updateField('Touchbase_Timing__c', e.target.value)}>
							{ picklists['Touchbase_Timing__c'].map((opt, i) => (<option key={i} value={opt.value}>{opt.label}</option>)) }
						</select>
					</EditField>

					<EditField title="Last Touchbase">
						<DatePicker
							className="date-field-styles"
							placeholderText="Pick Date"
							selected={editHeatmap.Last_Touchbase__c}
							onChange={date => updateField('Last_Touchbase__c', date)}
							popperClassName="datePickerCalendar"
							disabledKeyboardNavigation
							monthsShown={1}
							dateFormat="P"
						/>
					</EditField>
					<EditField title="Next Touchbase">
						<DatePicker
							className="date-field-styles"
							placeholderText="Pick Date"
							selected={editHeatmap.Next_Touchbase__c}
							onChange={date => updateField('Next_Touchbase__c', date)}
							popperClassName="datePickerCalendar"
							disabledKeyboardNavigation
							monthsShown={1}
							dateFormat="P"
						/>
					</EditField>
				</EditFieldGroup>

				<EditFieldGroup title="Activity">
					<EditField title="Next Step">
						<input type="text" className="edit-field-styles" value={editHeatmap.Next_Step__c || ''} onChange={(e) => updateField('Next_Step__c', e.target.value)} />
					</EditField>
					<EditField title="Next Step Date">
						<DatePicker
							className="date-field-styles"
							placeholderText="Pick Date"
							selected={editHeatmap.Next_Step_Date__c}
							onChange={date => updateField('Next_Step_Date__c', date)}
							popperClassName="datePickerCalendar"
							disabledKeyboardNavigation
							monthsShown={1}
							dateFormat="P"
						/>
					</EditField>
					<EditField title="Next Step POC">
								<ContactSelect reset={resetDropdowns} value={nextStepPOC} onChange={onChangeNextStepPOC} disabled={selectedVendor === '-1'} vendor={selectedVendor} />
					</EditField>
				</EditFieldGroup>
			</div>
		</>
	)
});

export default EditHeatmap;
